import { omit } from '../../utils/props';
import { hasCurrencyCode, hasPurchaseRequest } from '../../utils/purchaseMotionTypeGuards';
import { getIsUpgradePoint } from '../../utils/queryParams';
import { ErrorBoundary, ErrorBoundaryNames } from '../ErrorBoundary';
import { AssignSeatButton } from '../PurchaseMotions/AssignSeatButton/AssignSeatButton';
import { BuyingButton } from '../PurchaseMotions/BuyingButton/BuyingButton';
import { PricingPageRedirectButton } from '../PurchaseMotions/PricingPageRedirectButton/PricingPageRedirectButton';
import { RequestProductButton } from '../PurchaseMotions/RequestProductButton/RequestProductButton';
import { RequestSeatButton } from '../PurchaseMotions/RequestSeatButton/RequestSeatButton';
import { TalkToSalesButton } from '../PurchaseMotions/TalkToSalesButton/TalkToSalesButton';
import { TrialButton } from '../PurchaseMotions/TrialButton/TrialButton';
import { ViewAccountButton } from '../PurchaseMotions/ViewAccountButton/ViewAccountButton';
import { jsx as _jsx } from "react/jsx-runtime";
export const PurchaseMotionCTABase = ({
  apiName,
  ctaProps,
  location,
  purchaseMotion,
  uiButtonProps = {},
  trackingData,
  onClick,
  upgradeConfig,
  children,
  'data-test-id': dataTestId,
  'data-selenium-id': dataSeleniumId,
  isUpgradePoint: originalIsUpgradePoint
}) => {
  //default to primary if not specified
  uiButtonProps = Object.assign({
    use: 'primary'
  }, uiButtonProps);
  const filteredUiButtonProps = omit(uiButtonProps, ['children', 'className', 'external', 'href', 'onClick', 'style']);
  const isUpgradePoint = originalIsUpgradePoint !== null && originalIsUpgradePoint !== void 0 ? originalIsUpgradePoint : getIsUpgradePoint(location);
  switch (purchaseMotion) {
    case 'ASSIGN_SEAT':
      return /*#__PURE__*/_jsx(AssignSeatButton, Object.assign({
        apiName: apiName,
        location: location,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick
      }, ctaProps, {
        children: children
      }));
    case 'BUY_ADDITIONAL_CONTACTS':
    case 'BUY_ADDITIONAL_SEATS':
    case 'BUY_TOUCHLESS':
      if (hasPurchaseRequest(ctaProps)) {
        return /*#__PURE__*/_jsx(BuyingButton, Object.assign({
          purchaseMotion: purchaseMotion,
          apiName: apiName,
          location: location,
          trackingData: trackingData,
          upgradeConfig: upgradeConfig,
          "data-test-id": dataTestId,
          "data-selenium-id": dataSeleniumId,
          uiButtonProps: filteredUiButtonProps,
          onClick: onClick,
          isUpgradePoint: isUpgradePoint
        }, ctaProps, {
          children: children
        }));
      } else if (hasCurrencyCode(ctaProps)) {
        return /*#__PURE__*/_jsx(BuyingButton, Object.assign({
          purchaseMotion: purchaseMotion,
          apiName: apiName,
          location: location,
          trackingData: trackingData,
          upgradeConfig: upgradeConfig,
          "data-test-id": dataTestId,
          "data-selenium-id": dataSeleniumId,
          uiButtonProps: filteredUiButtonProps,
          onClick: onClick,
          isUpgradePoint: isUpgradePoint
        }, ctaProps, {
          children: children
        }));
      } else {
        throw new Error('buying button props must have either purchaseRequest or currencyCode');
      }
    case 'PRICING_PAGE_REDIRECT':
      return /*#__PURE__*/_jsx(PricingPageRedirectButton, Object.assign({
        apiName: apiName,
        location: location,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isUpgradePoint: isUpgradePoint
      }, ctaProps, {
        children: children
      }));
    case 'REQUEST_SEAT':
      return /*#__PURE__*/_jsx(RequestSeatButton, Object.assign({
        apiName: apiName,
        location: location,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isUpgradePoint: isUpgradePoint
      }, ctaProps, {
        children: children
      }));
    case 'REQUEST_PRODUCT':
      return /*#__PURE__*/_jsx(RequestProductButton, Object.assign({
        apiName: apiName,
        location: location,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isUpgradePoint: isUpgradePoint
      }, ctaProps, {
        children: children
      }));
    case 'TALK_TO_SALES':
      return /*#__PURE__*/_jsx(TalkToSalesButton, Object.assign({
        apiName: apiName,
        location: location,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isAssignable: true
      }, ctaProps, {
        children: children
      }));
    case 'TALK_TO_SALES_FALLBACK':
      return /*#__PURE__*/_jsx(TalkToSalesButton, Object.assign({
        apiName: apiName,
        location: location,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isAssignable: false
      }, ctaProps, {
        children: children
      }));
    case 'TRIAL':
      return /*#__PURE__*/_jsx(TrialButton, Object.assign({
        apiName: apiName,
        location: location,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isUpgradePoint: isUpgradePoint
      }, ctaProps, {
        children: children
      }));
    case 'VIEW_ACCOUNT':
      return /*#__PURE__*/_jsx(ViewAccountButton, Object.assign({
        apiName: apiName,
        location: location,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick
      }, ctaProps, {
        children: children
      }));
    default:
      return null;
  }
};
export const PurchaseMotionCTA = props => {
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    boundaryName: ErrorBoundaryNames.PURCHASE_MOTION_CTA,
    children: /*#__PURE__*/_jsx(PurchaseMotionCTABase, Object.assign({}, props))
  });
};